/**
 * inviteUtils.ts — invite validation and redemption utility
 *
 * DATA MODEL NOTE (confirmed from bandMembershipService.js / firestore.rules):
 *
 *   invites/{token}  — the invite CODE is the Firestore document ID, NOT a
 *   field named "code". There are no maxUses / uses fields; redemption is
 *   single-use and tracked by the `usedBy` field (null = unused).
 *
 *   Invite doc fields:
 *     bandId    : string
 *     createdBy : string
 *     createdAt : Timestamp
 *     expiresAt : Timestamp   (always present)
 *     usedBy    : string | null
 *     usedAt    : Timestamp | null
 *
 *   Member doc (bands/{bandId}/members/{uid}) requires `claimedVia` for the
 *   invite-gated create path enforced by firestore.rules — the spec's
 *   `inviteCode` field name is replaced with `claimedVia` to satisfy the rules.
 *
 * SCOPE NOTE:
 *   redeemInviteCode() validates and writes the member doc only. It does NOT:
 *     - mark the invite as used (usedBy / usedAt)
 *     - update users/{uid}/bands/{bandId}
 *     - update users/{uid}.lastActiveBandId
 *     - refresh the Firebase Auth token
 *   The full atomic flow for those steps lives in joinBandWithInvite()
 *   (bandMembershipService.js), which wraps everything in a runTransaction.
 */

import type { User } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export type InvitePreviewStatus = "valid" | "invalid" | "expired" | "consumed";

export interface InvitePreviewResult {
  inviteStatus: InvitePreviewStatus;
  bandName: string;
  reasonCode: string;
  expiresAt: string;
}

const getInvitePreviewEndpoint = () => {
  const explicitUrl = (process.env.REACT_APP_INVITE_PREVIEW_URL || "").trim();
  if (explicitUrl) return explicitUrl;

  const projectId = (process.env.REACT_APP_FIREBASE_PROJECT_ID || "").trim();
  if (!projectId) return "";

  const isLocalHost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  if (isLocalHost) {
    return `http://127.0.0.1:5001/${projectId}/us-central1/previewInvite`;
  }

  return `https://us-central1-${projectId}.cloudfunctions.net/previewInvite`;
};

export async function fetchInvitePreview(code: string): Promise<InvitePreviewResult> {
  const normalizedCode = code.trim().toUpperCase();
  if (!normalizedCode) {
    return {
      inviteStatus: "invalid",
      bandName: "",
      reasonCode: "MISSING_CODE",
      expiresAt: "",
    };
  }

  const endpoint = getInvitePreviewEndpoint();
  if (!endpoint) {
    return {
      inviteStatus: "invalid",
      bandName: "",
      reasonCode: "PREVIEW_ENDPOINT_MISSING",
      expiresAt: "",
    };
  }

  try {
    const response = await fetch(`${endpoint}?code=${encodeURIComponent(normalizedCode)}`);
    if (!response.ok) {
      return {
        inviteStatus: "invalid",
        bandName: "",
        reasonCode: "PREVIEW_UNAVAILABLE",
        expiresAt: "",
      };
    }

    const data = await response.json();
    const inviteStatus: InvitePreviewStatus =
      data?.inviteStatus === "valid" ||
      data?.inviteStatus === "expired" ||
      data?.inviteStatus === "consumed"
        ? data.inviteStatus
        : "invalid";

    return {
      inviteStatus,
      bandName: typeof data?.bandName === "string" ? data.bandName : "",
      reasonCode: typeof data?.reasonCode === "string" ? data.reasonCode : "",
      expiresAt: typeof data?.expiresAt === "string" ? data.expiresAt : "",
    };
  } catch {
    return {
      inviteStatus: "invalid",
      bandName: "",
      reasonCode: "PREVIEW_UNAVAILABLE",
      expiresAt: "",
    };
  }
}

// ── Error enum ────────────────────────────────────────────────────────────────

export enum InviteError {
  INVALID_CODE   = "INVALID_CODE",
  EXPIRED_CODE   = "EXPIRED_CODE",
  ALREADY_MEMBER = "ALREADY_MEMBER",
  CODE_EXHAUSTED = "CODE_EXHAUSTED",
  NETWORK_ERROR  = "NETWORK_ERROR",
}

// ── Internal types ────────────────────────────────────────────────────────────

interface InviteDoc {
  bandId: string;
  expiresAt: { toDate(): Date };
  usedBy: string | null;
}

interface BandDoc {
  name?: string;
}

// ── redeemInviteCode ──────────────────────────────────────────────────────────

/**
 * Validates an invite code and writes the caller as a member of the target band.
 *
 * Validation order:
 *   1. Invite exists (getDoc by document ID — the code IS the ID)
 *   2. expiresAt not in the past
 *   3. usedBy is null (single-use exhaustion check)
 *   4. User is not already a member
 *   5. Write bands/{bandId}/members/{uid}
 *
 * Throws an `Error` whose `.message` is one of the `InviteError` enum values.
 * Any Firestore / network failure that is not a known validation error is
 * surfaced as `InviteError.NETWORK_ERROR`.
 */
export async function redeemInviteCode(
  user: User,
  code: string,
): Promise<{ bandId: string; bandName: string }> {
  const normalizedCode = code.trim();
  const knownErrors = new Set<string>(Object.values(InviteError));

  try {
    // 1. Look up the invite — the token is the document ID, not a queryable field.
    const inviteSnap = await getDoc(doc(db, "invites", normalizedCode));

    if (!inviteSnap.exists()) {
      throw new Error(InviteError.INVALID_CODE);
    }

    const invite = inviteSnap.data() as InviteDoc;

    // 2. Expiry check.
    if (invite.expiresAt.toDate() < new Date()) {
      throw new Error(InviteError.EXPIRED_CODE);
    }

    // 3. Single-use exhaustion check (usedBy tracks the one allowed redemption).
    if (invite.usedBy !== null) {
      throw new Error(InviteError.CODE_EXHAUSTED);
    }

    const { bandId } = invite;

    // 4. Existing-membership check (isSelf rule allows reading own member doc).
    const memberRef = doc(db, "bands", bandId, "members", user.uid);
    const memberSnap = await getDoc(memberRef);

    if (memberSnap.exists()) {
      throw new Error(InviteError.ALREADY_MEMBER);
    }

    // Fetch band name for the return value (any signed-in user may getDoc a band).
    const bandSnap = await getDoc(doc(db, "bands", bandId));
    const bandName = bandSnap.exists()
      ? (bandSnap.data() as BandDoc).name ?? ""
      : "";

    // 5. Write membership.
    // `claimedVia` is required by firestore.rules for the invite-gated member
    // create path — the field name differs from the spec's `inviteCode` suggestion.
    await setDoc(memberRef, {
      role: "member",
      claimedVia: normalizedCode,
      joinedAt: serverTimestamp(),
    });

    return { bandId, bandName };
  } catch (err) {
    // Pass through errors we threw ourselves; wrap everything else.
    if (err instanceof Error && knownErrors.has(err.message)) {
      throw err;
    }
    throw new Error(InviteError.NETWORK_ERROR);
  }
}
